import React from 'react';
import { Card } from '@frontend/shadcn/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@frontend/shadcn/components/ui/tooltip';
import { CircleInfoIcon } from '@revfluence/fresh-icons/solid/esm';

import {
  TooltipHeader,
  TooltipTitle,
  TooltipBody,
  TooltipItem,
  TooltipItemValue,
  TooltipArrow,
} from '@frontend/applications/Social/components/tooltip';
import { cn } from '@/shadcn/lib/utils';

interface WidgetContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  widgetTitle: string;
  widgetIcon?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
  textColor?: string;
  bgColor?: string;
  widgetActions?: React.ReactNode;
  tooltip?: string;
}

const WidgetContainer: React.FC<WidgetContainerProps> = ({
  widgetTitle,
  widgetIcon,
  className,
  children,
  textColor = 'text-white',
  bgColor = 'bg-primary',
  widgetActions,
  tooltip,
  ...props
}) => (
  <Card {...props} className={cn('rounded-2xl h-full flex flex-col', className)}>
    <div className={cn('p-3 flex justify-between rounded-t-2xl items-center', bgColor)}>
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          {widgetIcon}
          <span className={cn('text-base font-semibold', textColor)}>{widgetTitle}</span>
        </div>
        {tooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleInfoIcon className={cn('cursor-pointer', 'text-grey-0')} fontSize={14} />
            </TooltipTrigger>
            <TooltipContent
              className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]"
              side="bottom"
              align="start"
              sideOffset={5}
              role="tooltip"
              aria-live="polite"
            >
              <TooltipHeader>
                <TooltipTitle>{widgetTitle}</TooltipTitle>
              </TooltipHeader>
              <TooltipBody>
                <TooltipItem>
                  <TooltipItemValue>
                    {tooltip}
                  </TooltipItemValue>
                </TooltipItem>
              </TooltipBody>
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="flex gap-1 items-center">
        {widgetActions && widgetActions}
      </div>
    </div>
    <div className="h-px bg-gray-200" />
    <div className="p-4 bg-white flex-grow rounded-b-2xl">{children}</div>
  </Card>
);

export default WidgetContainer;
